import type { ApiClient } from './client';

export interface ScriptRole {
  id: string;
  name: string;
  voice_id: number | null;
  voice_source: 'default' | 'manual' | 'character';
}

export interface ScriptLine {
  id: string;
  role_id: string;
  text: string;
}

export interface ScriptProjectCard {
  id: number;
  name: string;
  roles_count: number;
  lines_count: number;
  duration_ms: number | null;
  revision: number;
  schema_version: 1;
  updated_at: string;
}

export interface ScriptProject extends ScriptProjectCard {
  created_at: string;
  roles: ScriptRole[];
  lines: ScriptLine[];
}

export interface ScriptProjectPage {
  items: ScriptProjectCard[];
  next_cursor: string | null;
}

export interface ScriptProjectEdit {
  name?: string;
  roles?: ScriptRole[];
  lines?: ScriptLine[];
}

export interface ScriptQuote {
  project_id: number;
  project_revision?: number;
  result_revision?: number;
  line_key?: string | null;
  amount: number;
  currency: 'Circle';
  affordable: boolean;
  price_policy_version?: string;
}

export interface ScriptTimelineLine {
  line_key: string;
  role_key: string;
  role_name?: string;
  text?: string;
  voice_id?: number;
  start_ms: number;
  duration_ms: number;
}

export interface ScriptOperation {
  operation_id: string;
  project_id: number;
  project_revision: number;
  line_id?: string;
  order_id?: number;
  result_revision?: number;
  status: 'blocked' | 'queued' | 'processing' | 'ready' | 'failed' | 'uncertain';
  error_code?: string | null;
  audio_url?: string | null;
  duration_ms?: number | null;
  poll_after_ms?: number | null;
  amount_tokens?: string;
  timeline?: ScriptTimelineLine[] | null;
  source_lines?: Array<{ line_key: string; role_name: string; text: string; voice_id: number }>;
  total_lines?: number;
  completed_lines?: number;
  progress?: { completed: number; total: number; stage: 'synthesis' | 'assembly' | 'finance' | 'done' };
  created_at: string;
}

/** Аудио читается только из owner-scoped API, без подписанных ссылок провайдера. */
export function scriptAudioPath(projectId: number, operationId: string) {
  if (
    !Number.isSafeInteger(projectId) ||
    projectId < 1 ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operationId)
  )
    throw new Error('script_audio_invalid');
  return `/app/script/projects/${projectId}/operations/${operationId}/audio`;
}

/** Черновик и платные операции привязаны к точной ревизии проекта. */
export const scripts = (client: ApiClient) => ({
  capabilities: (signal?: AbortSignal) =>
    client.request<{
      script: {
        read_enabled: boolean;
        write_enabled: boolean;
        preview_enabled: boolean;
        render_enabled: boolean;
        revoice_enabled: boolean;
      };
    }>('/app/capabilities', { signal }),
  list: (cursor?: string, limit = 25, signal?: AbortSignal) =>
    client.request<ScriptProjectPage>('/app/script/projects', { query: { cursor, limit }, signal }),
  get: (id: number, signal?: AbortSignal) => client.request<ScriptProject>(`/app/script/projects/${id}`, { signal }),
  create: (edit: ScriptProjectEdit, idempotencyKey: string, signal?: AbortSignal) =>
    client.request<ScriptProject>('/app/script/projects', {
      method: 'POST',
      body: { ...edit, schema_version: 1 },
      idempotency: idempotencyKey,
      signal,
    }),
  save: (id: number, revision: number, edit: ScriptProjectEdit, signal?: AbortSignal) =>
    client.request<ScriptProject>(`/app/script/projects/${id}`, {
      method: 'PATCH',
      body: { ...edit, schema_version: 1 },
      ifMatch: `"${revision}"`,
      signal,
    }),
  remove: (id: number, revision: number, signal?: AbortSignal) =>
    client.request<void>(`/app/script/projects/${id}`, {
      method: 'DELETE',
      ifMatch: `"${revision}"`,
      signal,
    }),
  quote: (id: number, revision: number, lineKey?: string, signal?: AbortSignal) =>
    client.request<ScriptQuote>(`/app/script/projects/${id}/quote`, {
      query: { projectRevision: revision, lineKey },
      signal,
    }),
  preview: (id: number, revision: number, lineKey: string, price: number, key: string, signal?: AbortSignal) =>
    client.request<ScriptOperation>(`/app/script/projects/${id}/lines/${encodeURIComponent(lineKey)}/preview`, {
      method: 'POST',
      // pause_ms и format не выбираются человеком, но уходят в заказ (03-api §9.2, APP-SPEC-5 §5)
      body: { expected_price: price, pause_ms: 600, format: 'mp3' },
      ifMatch: `"${revision}"`,
      idempotency: key,
      signal,
    }),
  render: (id: number, revision: number, price: number, key: string, signal?: AbortSignal) =>
    client.request<ScriptOperation>(`/app/script/projects/${id}/render`, {
      method: 'POST',
      body: { expected_price: price },
      ifMatch: `"${revision}"`,
      idempotency: key,
      signal,
    }),
  operation: (id: number, operationId: string, signal?: AbortSignal) =>
    client.request<ScriptOperation>(`/app/script/projects/${id}/operations/${encodeURIComponent(operationId)}`, { signal }),
  latest: (id: number, signal?: AbortSignal) =>
    client.request<{
      project_id: number;
      latest_render: ScriptOperation | null;
      latest_ready_render: ScriptOperation | null;
    }>(`/app/script/projects/${id}/operations/latest`, { signal }),
  audio: (id: number, operationId: string, signal?: AbortSignal) => client.download(scriptAudioPath(id, operationId), signal),
  revoiceQuote: (id: number, orderId: number, lineKey: string, revision: number, signal?: AbortSignal) =>
    client.request<ScriptQuote>(`/app/script/projects/${id}/orders/${orderId}/lines/${encodeURIComponent(lineKey)}/quote`, {
      query: { resultRevision: revision },
      signal,
    }),
  revoice: (id: number, orderId: number, lineKey: string, revision: number, price: number, key: string, signal?: AbortSignal) =>
    client.request<ScriptOperation>(`/app/script/projects/${id}/orders/${orderId}/lines/${encodeURIComponent(lineKey)}/revoice`, {
      method: 'POST',
      body: { expected_price: price, expected_result_revision: revision },
      idempotency: key,
      signal,
    }),
});
